import styled, { keyframes, css } from 'styled-components';
import { ContentCategory, ImageSource, SocialNetwork, AIModel } from '@agency-smm/shared';
import { Card } from '../Card';

// ─── Layout ─────────────────────────────────────────────────────────────────

export const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.md};
`;

export const Title = styled.h1`
  font-size: ${({ theme }) => theme.fontSize.xxxl};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
`;

export const Subtitle = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-top: ${({ theme }) => theme.spacing.xs};
`;

// Layout vertical: Novah (el chat de configuración) arriba como barra de
// comando, y abajo la galería de posts a lo grande — es una pantalla 100% visual.
export const Grid = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg};
`;

// Bloque superior con Novah. Centrado y con ancho máximo para que el chat no
// se estire en pantallas anchas (los posts de abajo sí ocupan todo el ancho).
export const GenerateTopBar = styled.div`
  width: 100%;
  max-width: 920px;
  margin: 0 auto;

  // En pantallas chicas el chat ocupa todo el ancho y apila en columna para no
  // desbordar / verse apretado en ~375px.
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    flex-direction: column;
    max-width: 100%;
  }
`;

// ─── Fondo de partículas ──────────────────────────────────────────────────────
// Raíz posicionada para anclar el backdrop de partículas detrás del contenido.
export const GenerateRoot = styled.div`
  position: relative;
`;

// Capa del ParticleField. `sticky` + `margin-bottom: -100vh` la fija al viewport
// sin ocupar espacio en el flujo, así el canvas mide 100vh (acotado, sin crecer
// con el scroll del grid) y las partículas se ven fijas detrás del contenido.
export const ParticleBackdrop = styled.div`
  position: sticky;
  top: 0;
  height: 100vh;
  margin-bottom: -100vh;
  z-index: 0;
  pointer-events: none;
  overflow: hidden;
`;

export const GenerateContent = styled.div`
  position: relative;
  z-index: 1;
`;

// Cuerpo de /generate (header + barra Novah + grid). Cuando arranca la
// generación "retrocede" (escala, opacidad, blur) mientras la escena de
// generación entra por encima — transición de escena, sin modal.
export const GenerateBody = styled.div<{ $receded?: boolean }>`
  transition: transform 0.45s ease, opacity 0.45s ease, filter 0.45s ease;
  ${({ $receded }) => $receded && css`
    transform: scale(0.97);
    opacity: 0.35;
    filter: blur(3px);
    pointer-events: none;
  `}
  @media (prefers-reduced-motion: reduce) {
    transition: opacity 0.2s ease;
    transform: none;
    filter: none;
  }
`;

// Escena de generación a pantalla completa (dentro de /generate, NO es un modal
// fijo): cubre la superficie con un fondo oscuro y centra el loader inline. La
// entrada/salida la anima GSAP (ver GenerateScene). Arranca invisible.
export const SceneRoot = styled.div`
  position: absolute;
  inset: 0;
  z-index: 6;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: clamp(32px, 9vh, 130px) 24px 48px;
  border-radius: 12px;
  opacity: 0;
  transform: scale(0.98);
  will-change: opacity, transform;
  background: radial-gradient(circle at 50% 36%, rgba(14,18,48,0.82), rgba(10,11,22,0.96));
  backdrop-filter: blur(2px);
`;

// ─── Legacy platform / image source / color picker / asset selector ────────

export const SocialNetworkGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
`;

export const PlatformRow = styled.div<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border: 1px solid ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.border)};
  border-radius: 8px;
  background-color: ${({ theme, $active }) => ($active ? theme.colors.warning : 'transparent')};
  transition: transform 0.2s ease, box-shadow 0.2s ease;
`;

export const PlatformLabel = styled.label`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  flex: 1;
  cursor: pointer;

  input[type="checkbox"] {
    width: 16px;
    height: 16px;
    cursor: pointer;
  }
`;

export const PlatformName = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
`;

export const CountInput = styled.input`
  width: 60px;
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  font-size: ${({ theme }) => theme.fontSize.sm};
  text-align: center;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }

  &:disabled {
    background-color: ${({ theme }) => theme.colors.surfaceHover};
    cursor: not-allowed;
  }
`;

export const ImageSourceSection = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

export const ImageSourceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${({ theme }) => theme.spacing.sm};
`;

export const ImageSourceOption = styled.button<{ $active: boolean; $disabled?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.md};
  border: 1px solid ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.border)};
  border-radius: 8px;
  background-color: ${({ theme, $active }) => ($active ? theme.colors.warning : 'transparent')};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  font-family: inherit;

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.colors.primary};
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

export const ImageSourceIcon = styled.span`
  font-size: 24px;
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

export const ImageSourceLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  text-align: center;
`;

export const ImageSourceDesc = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  text-align: center;
  margin-top: 2px;
`;

export const ImageMixSlider = styled.div`
  margin-top: ${({ theme }) => theme.spacing.md};
`;

export const SliderRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

export const SliderLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  min-width: 80px;
`;

export const SliderValue = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  min-width: 35px;
  text-align: right;
`;

export const RangeSlider = styled.input`
  flex: 1;
  height: 6px;
  -webkit-appearance: none;
  background: ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  outline: none;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    width: 16px;
    height: 16px;
    background: ${({ theme }) => theme.colors.primary};
    border-radius: 8px;
    cursor: pointer;
  }
`;

export const AssetSelectorContainer = styled.div`
  margin-top: ${({ theme }) => theme.spacing.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  overflow: hidden;
`;

export const AssetSelectorHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.sm};
  background: ${({ theme }) => theme.colors.surfaceHover};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

export const AssetSelectorTitle = styled.span`
  font-size: 0.85rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

export const AssetSelectorButtons = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.xs};
`;

export const SmallButton = styled.button`
  padding: 4px 8px;
  font-size: 0.75rem;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.primary};
    color: white;
    border-color: ${({ theme }) => theme.colors.border};
  }
`;

export const AssetSelector = styled.div`
  max-height: 150px;
  overflow-y: auto;
`;

export const AssetItem = styled.label<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.sm};
  cursor: pointer;
  background-color: ${({ theme, $selected }) => ($selected ? theme.colors.warning : 'transparent')};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  &:last-child { border-bottom: none; }
  &:hover { background-color: ${({ theme }) => theme.colors.surfaceHover}; }

  input { display: none; }
`;

export const AssetThumb = styled.img`
  width: 40px;
  height: 40px;
  object-fit: cover;
  border-radius: 8px;
  border: 2px solid ${({ theme }) => theme.colors.border};
`;

export const AssetName = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm};
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const ColorPickerContainer = styled.div`
  margin-top: ${({ theme }) => theme.spacing.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  overflow: hidden;
`;

export const ColorPickerHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.sm};
  background: ${({ theme }) => theme.colors.surfaceHover};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

export const ColorPickerTitle = styled.span`
  font-size: 0.85rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

export const ColorInputRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.sm};
`;

export const ColorInput = styled.input`
  width: 40px;
  height: 40px;
  padding: 0;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  cursor: pointer;
  background: transparent;

  &::-webkit-color-swatch-wrapper { padding: 0; }
  &::-webkit-color-swatch { border: none; border-radius: 8px; }
`;

export const AddColorButton = styled.button`
  padding: 8px 16px;
  font-size: 0.85rem;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover { transform: translateY(-2px); box-shadow: ${({ theme }) => theme.shadows.sm}; }
`;

export const SelectedColorsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.xs};
  padding: ${({ theme }) => theme.spacing.sm};
  min-height: 50px;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

export const ColorChip = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  font-size: 0.75rem;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
`;

export const ColorSwatch = styled.span<{ $color: string }>`
  width: 16px;
  height: 16px;
  border-radius: 8px;
  background-color: ${({ $color }) => $color};
  border: 2px solid ${({ theme }) => theme.colors.border};
`;

export const ColorNumber = styled.span`
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textSecondary};
  min-width: 16px;
`;

export const RemoveColorButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  padding: 0;
  border: 2px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surfaceHover};
  border-radius: 8px;
  cursor: pointer;
  font-size: 10px;
  color: ${({ theme }) => theme.colors.textSecondary};

  &:hover { background: ${({ theme }) => theme.colors.error}; color: white; }
`;

export const NoColorsMessage = styled.span`
  font-size: 0.8rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-style: italic;
`;

// ─── Posts grid / cards ─────────────────────────────────────────────────────

export const CategoryBadge = styled.span<{ $category: ContentCategory }>`
  display: inline-block;
  padding: 1px 5px;
  border-radius: 6px;
  font-size: 10px;
  font-weight: 600;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background-color: ${({ $category }) => {
    switch ($category) {
      case 'education': return '#93C5FD';
      case 'connection': return '#D8B4FE';
      case 'proof': return '#86EFAC';
      case 'promotion': return '#FDE68A';
      default: return '#f5f5f5';
    }
  }};
  color: ${({ theme }) => theme.colors.text};
`;

// Grid masonry: columnas de ancho fijo + filas base chiquitas. El alto de cada
// carta lo reparte useMasonryLayout vía grid-row-end. `align-items: start` es
// obligatorio para que cada item mida su contenido (ver el hook). El gap
// vertical lo mete el hook en el span; acá solo el column-gap.
export const PostsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(100%, 280px), 1fr));
  grid-auto-rows: 8px;
  align-items: start;
  column-gap: ${({ theme }) => theme.spacing.lg};
`;

// Wrapper de cada carta dentro del masonry: es el grid-item que recibe el
// row-span y aloja el grip de drag. La carta va full-bleed adentro.
export const MasonryItem = styled.div<{ $dragging?: boolean }>`
  position: relative;
  align-self: start;
  opacity: ${({ $dragging }) => ($dragging ? 0.35 : 1)};
  transition: opacity 0.15s ease;
`;

// Grip para arrastrar la carta y reordenar. Aparece en hover, centrado arriba
// para no chocar con el checkbox (top-left) ni con los botones de acción
// (top-right).
export const DragHandle = styled.div`
  position: absolute;
  top: 6px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 6;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 20px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.72);
  color: #fff;
  font-size: 12px;
  letter-spacing: 2px;
  cursor: grab;
  opacity: 0;
  transition: opacity 0.15s ease;
  &:active { cursor: grabbing; }
  ${MasonryItem}:hover & { opacity: 1; }
`;

export const PostCardStyled = styled(Card)`
  overflow: hidden;
  cursor: pointer;
  padding: 0;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  &:hover { transform: translateY(-2px); box-shadow: ${({ theme }) => theme.shadows.md}; }
  position: relative;
`;

export const PostImage = styled.div<{ $aspectRatio?: number }>`
  width: 100%;
  aspect-ratio: ${({ $aspectRatio }) => $aspectRatio || (9 / 16)};
  overflow: hidden;
  position: relative;
  background: ${({ theme }) => theme.colors.surfaceHover};
  img { width: 100%; height: 100%; object-fit: contain; }
`;

export const HoverOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 12px;
  background: linear-gradient(transparent, rgba(0,0,0,0.8));
  color: white;
  opacity: 0;
  transition: opacity 0.2s ease;
  ${PostCardStyled}:hover & { opacity: 1; }
`;

export const PostCardFooter = styled.div`
  padding: 4px 8px;
  display: flex;
  gap: 3px;
  align-items: center;
  flex-wrap: wrap;
`;

// Preview del texto generado (caption/headline) siempre visible en la carta,
// arriba de los tags. Se recorta a 2 líneas.
export const CaptionPreview = styled.div`
  padding: 8px 10px 2px;
  font-size: 12.5px;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.textSecondary};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
`;

// Encabezado de la vista de generaciones (cuando el chat de Novah se oculta).
export const GenerationsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 16px;
`;

export const GenerationsTitle = styled.h2`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.xl};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
`;

export const GenerationsSub = styled.p`
  margin: 2px 0 0;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

export const SelectCheckbox = styled.div<{ $selected: boolean }>`
  position: absolute;
  top: 6px;
  left: 6px;
  width: 22px;
  height: 22px;
  border-radius: 4px;
  border: 2px solid ${({ $selected }) => $selected ? '#3B82F6' : 'rgba(255,255,255,0.7)'};
  background: ${({ $selected }) => $selected ? '#3B82F6' : 'rgba(0,0,0,0.3)'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 14px;
  cursor: pointer;
  z-index: 5;
  transition: all 0.15s;
  opacity: ${({ $selected }) => $selected ? 1 : 0};
  ${PostCardStyled}:hover & { opacity: 1; }
`;

// ─── Selection bar ──────────────────────────────────────────────────────────

export const SelectionBar = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  padding: 10px 16px;
  background: #3B82F6;
  color: white;
  border-radius: 8px;
  margin-bottom: 12px;
  font-size: 13px;
  font-weight: 600;
  flex-wrap: wrap;
`;

export const SelectionBarBtn = styled.button`
  padding: 4px 12px;
  border-radius: 6px;
  border: 1px solid rgba(255,255,255,0.5);
  background: transparent;
  color: white;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  &:hover { background: rgba(255,255,255,0.2); }
`;

export const SelectionTemplateSelect = styled.select`
  padding: 4px 8px;
  border-radius: 6px;
  border: 1px solid rgba(255,255,255,0.5);
  background: rgba(255,255,255,0.15);
  color: white;
  font-size: 12px;
  cursor: pointer;
  option { color: #333; background: white; }
`;

export const ImageSourceBadge = styled.span<{ $source: ImageSource }>`
  display: inline-block;
  padding: 1px 5px;
  border-radius: 6px;
  font-size: 10px;
  font-weight: 600;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background-color: ${({ $source }) => {
    switch ($source) {
      case 'stock': return '#93C5FD';
      case 'pastel': return '#D8B4FE';
      case 'asset': return '#86EFAC';
      case 'ai': return '#FDE68A';
      default: return '#f3f4f6';
    }
  }};
  color: ${({ theme }) => theme.colors.text};
`;

export const IMAGE_SOURCE_LABELS: Record<ImageSource, string> = {
  stock: 'Stock',
  pastel: 'Color',
  asset: 'Custom',
  ai: 'AI',
  'baked-ai': 'AI Baked',
};

export const AI_MODEL_LABELS: Record<NonNullable<AIModel>, string> = {
  'kie': 'KIE',
  'cloudflare-sd': 'SD',
  'cloudflare': 'CF Flux',
};

// Display label considerando AI model + detail si aplica.
export const getImageSourceLabel = (
  imageSource: ImageSource,
  aiModel?: AIModel | null,
  aiModelDetail?: string | null,
): string => {
  if (imageSource === 'ai') {
    if (aiModelDetail) return aiModelDetail;
    if (aiModel) return AI_MODEL_LABELS[aiModel] || 'AI';
    return 'AI';
  }
  if (imageSource === 'baked-ai') {
    return aiModelDetail ? `AI Baked · ${aiModelDetail}` : 'AI Baked';
  }
  return IMAGE_SOURCE_LABELS[imageSource];
};

// ─── Skeletons / streaming ──────────────────────────────────────────────────

const shimmer = keyframes`
  0% { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

export const SkeletonCard = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: ${({ theme }) => theme.borders.thin};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  overflow: hidden;
  box-shadow: ${({ theme }) => theme.shadows.sm};
`;

export const SkeletonImage = styled.div<{ $aspectRatio?: number }>`
  width: 100%;
  aspect-ratio: ${({ $aspectRatio }) => $aspectRatio || (9 / 16)};
  background: linear-gradient(90deg, ${({ theme }) => theme.colors.surfaceHover} 25%, ${({ theme }) => theme.colors.background} 50%, ${({ theme }) => theme.colors.surfaceHover} 75%);
  background-size: 800px 100%;
  animation: ${shimmer} 1.5s ease-in-out infinite;
`;

export const SkeletonLine = styled.div<{ $width?: string }>`
  height: 12px;
  width: ${({ $width }) => $width || '100%'};
  background: linear-gradient(90deg, ${({ theme }) => theme.colors.surfaceHover} 25%, ${({ theme }) => theme.colors.background} 50%, ${({ theme }) => theme.colors.surfaceHover} 75%);
  background-size: 800px 100%;
  animation: ${shimmer} 1.5s ease-in-out infinite;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
`;

export const SkeletonContent = styled.div`
  padding: ${({ theme }) => theme.spacing.md};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
`;

export const StreamProgressBarContainer = styled.div`
  width: 100%;
  background: ${({ theme }) => theme.colors.surfaceHover};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  height: 6px;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  overflow: hidden;
`;

export const StreamProgressBarFill = styled.div<{ $progress: number }>`
  height: 100%;
  width: ${({ $progress }) => $progress}%;
  background: linear-gradient(90deg, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.secondary});
  border-radius: ${({ theme }) => theme.borderRadius.full};
  transition: width 0.3s ease;
`;

export const StreamProgressText = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
`;

export const PastelBackground = styled.div<{ $color: string }>`
  width: 100%;
  height: 100%;
  background-color: ${({ $color }) => $color};
`;

// Placeholder mientras KIE genera la imagen del post: reproduce el clip neural
// (Remotion, variante cuadrada) a pantalla completa de la card + scrim + label.
export const AiLoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  background: #0A0B16;
  overflow: hidden;
`;

export const AiLoadingVideo = styled.video`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

export const AiLoadingScrim = styled.div`
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at 50% 50%, transparent 30%, rgba(8,9,20,0.55) 100%);
  pointer-events: none;
`;

// Spinner legacy (se mantiene por compat; el placeholder ahora usa el clip).
export const AiLoadingSpinner = styled.div`
  width: 32px;
  height: 32px;
  border: 1px solid ${({ theme }) => theme.colors.surface};
  border-top: 1px solid ${({ theme }) => theme.colors.primary};
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

export const AiLoadingText = styled.span`
  position: relative;
  z-index: 2;
  margin-bottom: 14px;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: #EEF1FF;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  letter-spacing: 0.3px;
  text-shadow: 0 2px 12px rgba(0,0,0,0.6);
`;

export const PostNetwork = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 10px;
  font-weight: 600;
  color: white;
  background: ${({ theme }) => theme.colors.primary};
  padding: 1px 6px;
  border-radius: 6px;
`;

export const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing.xxl};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

export const GeneratingOverlay = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.xxl};
  background-color: ${({ theme }) => theme.colors.surface};
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  box-shadow: ${({ theme }) => theme.shadows.md};
  text-align: center;
`;

export const Spinner = styled.div`
  width: 40px;
  height: 40px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-top-color: ${({ theme }) => theme.colors.primary};
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: ${({ theme }) => theme.spacing.md};

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

export const GeneratingText = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.fontSize.sm};
`;

// ─── Modals (regenerate + pick & cycle) ─────────────────────────────────────

export const RegenModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
`;

export const RegenModalContent = styled.div`
  background-color: ${({ theme }) => theme.colors.surface};
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  padding: ${({ theme }) => theme.spacing.xl};
  min-width: 300px;
  max-width: 400px;
`;

export const PickerModalContent = styled.div`
  background-color: ${({ theme }) => theme.colors.surface};
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  padding: ${({ theme }) => theme.spacing.lg};
  width: 560px;
  max-width: 92vw;
  max-height: 84vh;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
`;

export const PickerHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
`;

export const PickerSearch = styled.input`
  flex: 1;
  padding: 6px 10px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 6px;
  font-size: 13px;
  outline: none;
  &:focus { border-color: ${({ theme }) => theme.colors.primary}; }
`;

export const PickerList = styled.div`
  flex: 1;
  overflow-y: auto;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 6px;
`;

export const PickerRow = styled.label<{ $checked: boolean }>`
  display: grid;
  grid-template-columns: 24px 1fr 80px 110px;
  gap: 8px;
  align-items: center;
  padding: 8px 10px;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  cursor: pointer;
  background: ${({ $checked, theme }) => ($checked ? theme.colors.primary + '12' : 'transparent')};
  font-size: 13px;
  &:first-of-type { border-top: none; }
  &:hover { background: ${({ $checked, theme }) => ($checked ? theme.colors.primary + '20' : theme.colors.surfaceHover)}; }
`;

export const PickerBadge = styled.span<{ $kind: 'global' | 'user' | 'variant' }>`
  display: inline-block;
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  text-transform: uppercase;
  letter-spacing: 0.04em;
  background: ${({ $kind }) => $kind === 'global' ? '#DBEAFE' : $kind === 'variant' ? '#FEF3C7' : '#E0E7FF'};
  color: ${({ $kind }) => $kind === 'global' ? '#1E3A8A' : $kind === 'variant' ? '#92400E' : '#3730A3'};
`;

export const PickerFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

export const RegenModalTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  text-align: center;
`;

export const RegenOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
`;

export const RegenOption = styled.button<{ $loading?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  background-color: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  cursor: ${({ $loading }) => ($loading ? 'wait' : 'pointer')};
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  opacity: ${({ $loading }) => ($loading ? 0.7 : 1)};

  &:hover:not(:disabled) {
    background-color: ${({ theme }) => theme.colors.primary};
    border-color: ${({ theme }) => theme.colors.border};
    color: white;
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.sm};
  }

  &:disabled { cursor: not-allowed; opacity: 0.5; }
`;

export const RegenCancelButton = styled.button`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.sm};
  margin-top: ${({ theme }) => theme.spacing.md};
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.fontSize.sm};
  cursor: pointer;

  &:hover { color: ${({ theme }) => theme.colors.text}; }
`;

// ─── Constantes compartidas ─────────────────────────────────────────────────

export const SOCIAL_NETWORKS: { id: SocialNetwork; label: string }[] = [
  { id: 'instagram', label: 'Instagram' },
  { id: 'facebook', label: 'Facebook' },
  { id: 'twitter', label: 'Twitter/X' },
  { id: 'linkedin', label: 'LinkedIn' },
  { id: 'tiktok', label: 'TikTok' },
  { id: 'threads', label: 'Threads' },
  { id: 'gbp', label: 'Google Business' },
  { id: 'bluesky', label: 'Bluesky' },
];

// ─── Post EN ESPERA (cola de reintento) ─────────────────────────────────────
// Un post cuya generación reventó ya no se rehace degradado: ocupa la tarjeta
// entera diciendo qué pasó y qué va a pasar. Disimularlo lo haría pasar por un
// resultado normal, que es justo lo que estábamos arreglando.
export const RetryHold = styled.div<{ $exhausted?: boolean }>`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 14px;
  text-align: center;
  background: ${({ $exhausted }) => ($exhausted
    ? 'linear-gradient(160deg, #3F1D1D 0%, #1F1010 100%)'
    : 'linear-gradient(160deg, #1E293B 0%, #0F172A 100%)')};
`;

export const RetryBadge = styled.span`
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.06em;
  color: #FDE68A;
  white-space: nowrap;
`;

export const RetryText = styled.span`
  font-size: 11px;
  line-height: 1.45;
  color: rgba(255, 255, 255, 0.78);
  max-width: 22ch;
`;

export const RetryDetail = styled.span`
  font-size: 10px;
  line-height: 1.4;
  color: rgba(255, 255, 255, 0.5);
  max-width: 26ch;
  /* La causa cruda del proveedor puede ser larguísima: se acota a 3 líneas y el
     texto completo queda en el title. */
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
`;

export const RetryBtn = styled.button`
  margin-top: 4px;
  padding: 6px 14px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.28);
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  &:hover:not(:disabled) { background: rgba(255, 255, 255, 0.2); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;
